import { getWrestlerName } from './wrestlerUtils';
import React, { useState } from 'react';
import { PLES, getPLELogo } from './plesDatabase';
import { getPLETheme, getPLEBg } from './pleThemes';
import { renderMatchParticipants, ALL_RULE_TEAMS } from './ShowResults';
import STORYLINES from './storylinesDatabase';
import { TITLES_DATABASE } from './titlesDatabase';

const css = `
  * { box-sizing: border-box; }
  html { font-size: 133%; }
  .week-card { transition: background 0.15s, border-color 0.15s, transform 0.15s; cursor: pointer; }
  .week-card:hover { background: rgba(255,255,255,0.05) !important; }
  .week-card.selected { background: rgba(255,255,255,0.07) !important; border-color: rgba(255,255,255,0.3) !important; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 2px; }
`;

const WEEK_TYPE_LABELS = { regular: 'WEEKLY SHOW', gohome: 'GO-HOME', ple: 'PLE', wrestlemania: 'PLE' };
const WEEK_TYPE_COLORS = { regular: 'rgba(255,255,255,0.5)', gohome: '#e67e22', ple: '#9b59b6', wrestlemania: '#d4af37' };

const StarDisplay = ({ value, color }) => {
  if (!value) return <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.2)' }}>—</span>;
  const steps = [1,2,3,4,5];
  return (
    <div style={{ display: 'flex', gap: '1px', alignItems: 'center' }}>
      {steps.map(s => (
        <span key={s} style={{ color: value >= s ? (color || '#d4af37') : (value >= s - 0.5 ? (color || '#d4af37') : 'rgba(255,255,255,0.15)'), fontSize: '0.65rem' }}>
          {value >= s ? '★' : value >= s - 0.5 ? '⭑' : '★'}
        </span>
      ))}
      <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.4)', marginLeft: '4px' }}>{value}</span>
    </div>
  );
};

// Group weeks into PLE cycles (5 weeks each)
const groupByCycle = (weeks) => {
  const cycles = [];
  let current = [];
  weeks.forEach(w => {
    current.push(w);
    if (w.weekType === 'ple' || w.weekType === 'wrestlemania') {
      cycles.push([...current]);
      current = [];
    }
  });
  if (current.length > 0) cycles.push(current);
  return cycles;
};

export default function HistoryTab({ gameData, brandColor, onBack }) {
  const seasonHistory = gameData?.seasonHistory || [];
  const currentSeason = gameData?.season || 1;
  const pastRivalries = gameData?.pastRivalries || [];
  const roster = gameData?.roster || [];
  const [activeView, setActiveView] = useState('weeks'); // weeks | rivalries
  const [cardFilter, setCardFilter] = useState('all'); // all | matches | promos
  const [expandedRivalry, setExpandedRivalry] = useState(null);

  // Get unique seasons
  const seasons = [...new Set(seasonHistory.map(w => w.season))].sort((a, b) => a - b);
  const [selectedSeason, setSelectedSeason] = useState(seasons[seasons.length - 1] || 1);
  const [selectedWeek, setSelectedWeek] = useState(null);

  const seasonWeeks = seasonHistory.filter(w => w.season === selectedSeason);
  const cycles = groupByCycle(seasonWeeks);

  const bc = brandColor || '#d4af37';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{css}</style>

      {/* View toggle */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid rgba(255,255,255,0.08)', marginBottom: '20px', flexShrink: 0 }}>
        {[{ key: 'weeks', label: 'SHOW HISTORY' }, { key: 'rivalries', label: 'RIVALRIES (' + pastRivalries.length + ')' }].map(function(v) { return (
          <button key={v.key} onClick={() => setActiveView(v.key)}
            style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', letterSpacing: '1.5px', background: 'transparent', border: 'none', borderBottom: '2px solid ' + (activeView === v.key ? bc : 'transparent'), color: activeView === v.key ? '#fff' : 'rgba(255,255,255,0.35)', padding: '8px 20px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
            {v.label}
          </button>
        ); })}
      </div>

      {/* Past rivalries view */}
      {activeView === 'rivalries' && (
        <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {pastRivalries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px', fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)' }}>No past rivalries yet.</div>
          ) : (
            pastRivalries.map(function(r, ri) {
              var s1 = (r.side1 || []).map(function(id) {
                var w = roster.find(function(w) { return w.wrestler_id === id || w.wrestler_id === Number(id); });
                if (w) return w;
                var snap = (r.side1Snapshot || []).find(function(s) { return String(s.wrestler_id) === String(id); });
                return snap ? { ...snap, customRender: snap.render, _isSnapshot: true } : null;
              }).filter(Boolean);
              var s2 = (r.side2 || []).map(function(id) {
                var w = roster.find(function(w) { return w.wrestler_id === id || w.wrestler_id === Number(id); });
                if (w) return w;
                var snap = (r.side2Snapshot || []).find(function(s) { return String(s.wrestler_id) === String(id); });
                return snap ? { ...snap, customRender: snap.render, _isSnapshot: true } : null;
              }).filter(Boolean);
              var lvlColor = { 1: '#3498db', 2: '#e67e22', 3: '#e74c3c', 4: '#9b59b6' }[r.level] || '#fff';
              var lvlLabel = { 1: 'Niv.1 - Début', 2: 'Niv.2 - Intense', 3: 'Niv.3 - Chauffé', 4: 'Niv.4 - Blood Feud' }[r.level] || '';
              var storylineEntry = r.storylineId ? STORYLINES.find(function(s) { return s.id === r.storylineId; }) : null;
              var isExpanded = expandedRivalry === ri;
              var impactColors = { create: '#3498db', heatup: '#e67e22', cooldown: '#3498db', end: '#2ecc71', maintain: 'rgba(255,255,255,0.2)' };
              var impactLabels = { create: '⚡ CREATED', heatup: '🔥 HEAT UP', cooldown: '❄️ COOL DOWN', end: '✓ ENDED', maintain: '— MAINTAIN' };
              var events = (r.events || []).filter(function(ev) { return ev && ev.impact !== 'maintain'; });

              return (
                <div key={ri} style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid ' + (isExpanded ? lvlColor + '66' : lvlColor + '33'), borderRadius: '10px', overflow: 'hidden', transition: 'border-color 0.15s' }}>
                  <div style={{ height: '3px', background: 'linear-gradient(90deg, transparent, ' + lvlColor + ', transparent)' }} />

                  {/* Card header */}
                  <div style={{ padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {/* Renders */}
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                      {s1.map(function(w, wi) { return (
                        <img loading="lazy" key={wi} src={(w.customRender) || process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png'} alt=""
                          style={{ height: '60px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.8))' }} onError={function(e) { e.target.style.display='none'; }} />
                      ); })}
                    </div>
                    <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.75rem', color: '#e74c3c' }}>VS</div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px' }}>
                      {s2.map(function(w, wi) { return (
                        <img loading="lazy" key={wi} src={(w.customRender) || process.env.PUBLIC_URL + '/wrestlers/' + w.wrestler_id + '-render.png'} alt=""
                          style={{ height: '60px', objectFit: 'contain', objectPosition: 'top', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.8))' }} onError={function(e) { e.target.style.display='none'; }} />
                      ); })}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.6rem', color: '#fff', letterSpacing: '1px', marginBottom: '3px' }}>
                        {s1.map(function(w) { return w.name || getWrestlerName(w, 'display') || '?'; }).join(' & ')} <span style={{ color: '#e74c3c' }}>vs</span> {s2.map(function(w) { return w.name || getWrestlerName(w, 'display') || '?'; }).join(' & ')}
                      </div>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: lvlColor, background: lvlColor + '15', border: '1px solid ' + lvlColor + '44', borderRadius: '3px', padding: '1px 5px' }}>{lvlLabel}</span>
                        <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.3)' }}>W{r.startWeek} → W{r.endWeek} · {r.weeks} wk{r.weeks > 1 ? 's' : ''}</span>
                        {r.type === 'team' && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.05)', padding: '1px 5px', borderRadius: '3px' }}>TAG TEAM</span>}
                        {r.finalWinner && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: '#2ecc71' }}>✓ {(r.finalWinner.name || '').toUpperCase()}</span>}
                      </div>
                      {/* Storyline name */}
                      {storylineEntry && (
                        <div style={{ marginTop: '4px' }}>
                          <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: '#9b59b6', letterSpacing: '1px', background: 'rgba(155,89,182,0.1)', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '3px', padding: '1px 6px' }}>📖 {storylineEntry.name.toUpperCase()}</span>
                        </div>
                      )}
                    </div>

                    {/* Expand button */}
                    {events.length > 0 && (
                      <button onClick={function() { setExpandedRivalry(isExpanded ? null : ri); }}
                        style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', letterSpacing: '1px', background: isExpanded ? lvlColor + '22' : 'transparent', border: '1px solid ' + (isExpanded ? lvlColor + '55' : 'rgba(255,255,255,0.15)'), color: isExpanded ? lvlColor : 'rgba(255,255,255,0.4)', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}>
                        {isExpanded ? '▲ CLOSE' : '▼ HISTORY'}
                      </button>
                    )}
                  </div>

                  {/* Dropdown timeline */}
                  {isExpanded && (
                    <div style={{ borderTop: '1px solid ' + lvlColor + '33' }}>
                      {/* Storyline block if present */}
                      {storylineEntry && (
                        <div style={{ padding: '8px 14px', background: 'rgba(155,89,182,0.06)', borderBottom: '1px solid rgba(155,89,182,0.15)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '0.65rem' }}>📖</span>
                          <div>
                            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: 'rgba(155,89,182,0.6)', letterSpacing: '1px' }}>STORYLINE</div>
                            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.58rem', color: '#9b59b6', letterSpacing: '1.5px' }}>{storylineEntry.name.toUpperCase()}</div>
                          </div>
                        </div>
                      )}
                      {/* Events */}
                      {events.map(function(ev, ei) {
                        var evColor = impactColors[ev.impact] || '#fff';
                        var evLabel = impactLabels[ev.impact] || ev.impact;
                        var participantNames = (ev.participants || []).map(function(p) { return p.name || '?'; }).join(' & ');
                        var winnerName = ev.winner && ev.winner.name;
                        var phaseEntry = storylineEntry && ev.rivalryLevel
                          ? storylineEntry.phases.find(function(ph) { return ph.level === ev.rivalryLevel; }) : null;
                        return (
                          <div key={ei} style={{ padding: '8px 14px', borderBottom: ei < events.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none', background: ei % 2 === 0 ? 'rgba(0,0,0,0.2)' : 'transparent', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                            {/* Week dot */}
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: evColor + '14', border: '1px solid ' + evColor + '44', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.42rem', color: evColor }}>W{ev.week}</span>
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {/* Badges row */}
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '3px' }}>
                                <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: evColor, letterSpacing: '1px', border: '1px solid ' + evColor + '44', borderRadius: '3px', padding: '1px 5px', background: evColor + '0e' }}>{evLabel}</span>
                                {ev.isPromo
                                  ? <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.35rem', color: '#9b59b6', background: 'rgba(155,89,182,0.1)', border: '1px solid rgba(155,89,182,0.25)', borderRadius: '3px', padding: '1px 4px' }}>PROMO</span>
                                  : <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.35rem', color: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '3px', padding: '1px 4px' }}>MATCH</span>
                                }
                                {ev.matchType && ev.matchType !== 'normal' && (
                                  <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.35rem', color: '#e67e22', border: '1px solid rgba(230,126,34,0.3)', borderRadius: '3px', padding: '1px 4px' }}>{ev.matchType.replace(/-/g,' ').toUpperCase()}</span>
                                )}
                                {ev.titleChange && (
                                  <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.35rem', color: '#d4af37', border: '1px solid rgba(212,175,55,0.4)', borderRadius: '3px', padding: '1px 4px', background: 'rgba(212,175,55,0.08)' }}>👑 TITLE</span>
                                )}
                                {phaseEntry && (
                                  <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.35rem', color: 'rgba(155,89,182,0.6)' }}>· {phaseEntry.name.toUpperCase()}</span>
                                )}
                              </div>
                              {/* Participants + winner */}
                              {participantNames && (
                                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.65)' }}>
                                  {participantNames.toUpperCase()}
                                  {winnerName && !ev.noContest && <span style={{ color: '#2ecc71', marginLeft: '6px' }}>→ {winnerName.toUpperCase()}</span>}
                                  {ev.noContest && <span style={{ color: '#e74c3c', marginLeft: '6px' }}>NO CONTEST</span>}
                                </div>
                              )}
                              {/* Rating */}
                              {ev.rating > 0 && !ev.isPromo && <div style={{ marginTop: '2px' }}><StarDisplay value={ev.rating} color={lvlColor} /></div>}
                              {/* Notes */}
                              {ev.notes && <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(255,255,255,0.28)', fontStyle: 'italic', marginTop: '2px' }}>"{ev.notes}"</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {activeView === 'weeks' && (
      <div>
      {/* Season tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexShrink: 0, flexWrap: 'wrap' }}>
        {seasons.map(s => (
          <button key={s} onClick={() => { setSelectedSeason(s); setSelectedWeek(null); }}
            style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', letterSpacing: '2px', background: selectedSeason === s ? bc + '22' : 'transparent', border: '1px solid ' + (selectedSeason === s ? bc : 'rgba(255,255,255,0.15)'), color: selectedSeason === s ? bc : 'rgba(255,255,255,0.4)', padding: '7px 20px', borderRadius: '4px', cursor: 'pointer', transition: 'background 0.15s, border-color 0.15s, color 0.15s' }}>
            SEASON {s}
          </button>
        ))}
        {seasons.length === 0 && (
          <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)', padding: '8px 0', letterSpacing: '1px' }}>
            No history yet — complete your first show to start building history.
          </div>
        )}
      </div>

      {seasonWeeks.length > 0 && (
        <div style={{ flex: 1, display: 'flex', gap: '16px', overflow: 'hidden' }}>

          {/* LEFT — Cycles: horizontal row by default, vertical list when a week is selected */}
          <div style={{ flexShrink: 0, overflowX: selectedWeek ? 'visible' : 'auto', overflowY: selectedWeek ? 'auto' : 'visible', display: 'flex', flexDirection: selectedWeek ? 'column' : 'row', gap: '10px', maxWidth: selectedWeek ? '280px' : '100%', width: selectedWeek ? '280px' : '100%', transition: 'max-width 0.2s, width 0.2s', alignItems: 'flex-start' }}>
            {cycles.map((cycle, ci) => {
              const pleWeek = cycle.find(w => w.weekType === 'ple' || w.weekType === 'wrestlemania');
              const pleData = pleWeek && pleWeek.ple ? PLES.find(p => p.id === pleWeek.ple) : null;
              const cycleColor = pleWeek ? WEEK_TYPE_COLORS[pleWeek.weekType] : 'rgba(255,255,255,0.3)';
              const cycleHasSelected = selectedWeek && cycle.some(w => w.week === selectedWeek.week && w.season === selectedWeek.season);
              // When a week is selected in ANOTHER cycle, hide that cycle
              if (selectedWeek && !cycleHasSelected) return null;

              return (
                <div key={ci} style={{ background: 'rgba(8,8,8,0.85)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden', flexShrink: 0, width: selectedWeek ? '100%' : 'auto' }}>
                  {/* Cycle header - PLE */}
                  {(() => {
                    const pleTheme = pleData ? getPLETheme(pleData.id) : null;
                    const pleBg = pleData ? getPLEBg(pleData.id) : null;
                    const pleColor = pleTheme ? pleTheme.color : cycleColor;
                    return (
                      <div style={{ position: 'relative', overflow: 'hidden', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        {pleBg && <img loading="lazy" src={pleBg} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'blur(5px) brightness(0.25)', zIndex: 0 }} onError={e => { e.target.style.display='none'; }} />}
                        <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at bottom, ${pleColor}22 0%, transparent 70%)`, zIndex: 1 }} />
                        <div style={{ position: 'relative', zIndex: 2, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {pleData ? (
                            <>
                              <img loading="lazy" src={getPLELogo(pleData.id)} alt={pleData.name}
                                style={{ height: '36px', objectFit: 'contain', filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.8))' }}
                                onError={e => { e.target.style.display='none'; }} />
                              <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.58rem', letterSpacing: '2px', color: pleColor }}>{pleData.name.toUpperCase()}</div>
                            </>
                          ) : (
                            <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.58rem', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)' }}>
                              {pleWeek ? 'PLE' : 'IN PROGRESS...'}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Weeks — horizontal chips when collapsed, vertical list when expanded */}
                  <div style={{ padding: selectedWeek ? '6px' : '8px', display: 'flex', flexDirection: selectedWeek ? 'column' : 'column', gap: selectedWeek ? '2px' : '0' }}>
                    {cycle.map((w, wi) => {
                      const wColor = WEEK_TYPE_COLORS[w.weekType] || '#fff';
                      const isSelected = selectedWeek && selectedWeek.week === w.week && selectedWeek.season === w.season;
                      const isPLE = w.weekType === 'ple' || w.weekType === 'wrestlemania';
                      return (
                        <div key={wi} className={'week-card' + (isSelected ? ' selected' : '')}
                          onClick={() => setSelectedWeek(isSelected ? null : w)}
                          style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 10px', borderRadius: '6px', border: '1px solid ' + (isSelected ? wColor + '66' : 'transparent'), marginBottom: selectedWeek ? '0' : '3px', background: isSelected ? wColor + '14' : (isPLE ? wColor + '11' : 'transparent'), minWidth: selectedWeek ? 'auto' : '140px' }}>
                          {/* Week badge */}
                          <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: isPLE ? wColor + '22' : 'rgba(255,255,255,0.06)', border: '1px solid ' + (isPLE ? wColor + '66' : 'rgba(255,255,255,0.1)'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.52rem', color: isPLE ? wColor : 'rgba(255,255,255,0.5)' }}>{w.week}</span>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: wColor, letterSpacing: '1px' }}>{WEEK_TYPE_LABELS[w.weekType]}</div>
                            {w.avgRating && <StarDisplay value={w.avgRating} color={wColor} />}
                          </div>
                          {w.titleChanges && w.titleChanges.length > 0 && <span style={{ fontSize: '0.7rem' }}>👑</span>}
                          {w.rivalryEvents && w.rivalryEvents.length > 0 && <span style={{ fontSize: '0.65rem' }}>⚡</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* RIGHT — Week detail */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {selectedWeek ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {/* Week header */}
                <div style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '16px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '8px' }}>
                    <div style={{ fontFamily: "'Anton',sans-serif", fontSize: '1.4rem', letterSpacing: '3px', color: WEEK_TYPE_COLORS[selectedWeek.weekType] || '#fff' }}>
                      WEEK {selectedWeek.week}
                    </div>
                    <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: WEEK_TYPE_COLORS[selectedWeek.weekType], letterSpacing: '2px', background: (WEEK_TYPE_COLORS[selectedWeek.weekType] || '#fff') + '18', border: '1px solid ' + (WEEK_TYPE_COLORS[selectedWeek.weekType] || '#fff') + '44', borderRadius: '3px', padding: '2px 8px' }}>
                      {WEEK_TYPE_LABELS[selectedWeek.weekType]}
                    </div>
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {selectedWeek.avgRating && <StarDisplay value={selectedWeek.avgRating} color={bc} />}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)' }}>
                      Budget : <span style={{ color: '#2ecc71' }}>${(selectedWeek.budget || 0).toLocaleString('fr-FR')}</span>
                    </span>
                    <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)' }}>
                      Fans : <span style={{ color: '#3498db' }}>{(selectedWeek.fans || 0).toLocaleString('fr-FR')}</span>
                    </span>
                    {selectedWeek.profits != null && (
                      <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)' }}>
                        Show : <span style={{ color: selectedWeek.profits >= 0 ? '#2ecc71' : '#e74c3c' }}>{selectedWeek.profits >= 0 ? '+' : ''}${(selectedWeek.profits || 0).toLocaleString('fr-FR')}</span>
                      </span>
                    )}
                    {selectedWeek.matches && selectedWeek.matches.length > 0 && (
                      <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)' }}>
                        Matches : <span style={{ color: 'rgba(255,255,255,0.6)' }}>{selectedWeek.matches.length}</span>
                      </span>
                    )}
                    {selectedWeek.promos && selectedWeek.promos.length > 0 && (
                      <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: 'rgba(255,255,255,0.35)' }}>
                        Promos : <span style={{ color: '#9b59b6' }}>{selectedWeek.promos.length}</span>
                      </span>
                    )}
                  </div>
                </div>

                {/* Filter tabs */}
                <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                  {[['all','FULL SHOW'],['matches','MATCHES'],['promos','PROMOS']].map(function(f) { return (
                    <button key={f[0]} onClick={function() { setCardFilter(f[0]); }}
                      style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', letterSpacing: '1px', background: cardFilter === f[0] ? bc + '22' : 'transparent', border: '1px solid ' + (cardFilter === f[0] ? bc + '66' : 'rgba(255,255,255,0.1)'), color: cardFilter === f[0] ? bc : 'rgba(255,255,255,0.35)', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer' }}>
                      {f[1]}
                    </button>
                  ); })}
                </div>

                {/* Matches */}
                {cardFilter !== 'promos' && selectedWeek.matches && selectedWeek.matches.length > 0 && (
                  <div style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px', background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)', fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', letterSpacing: '2px', color: '#fff' }}>
                      MATCHES <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>({selectedWeek.matches.length})</span>
                    </div>
                    {selectedWeek.matches.map((m, i) => {
                      var mRule = m.matchRule || '1v1';
                      var teamSizes = ALL_RULE_TEAMS[mRule] || null;
                      var names = m.participants || [];
                      var teams;
                      if (teamSizes) {
                        teams = []; var off = 0;
                        teamSizes.forEach(function(sz) { teams.push(names.slice(off, off + sz)); off += sz; });
                      } else {
                        teams = names.map(function(n) { return [n]; });
                      }
                      // Flat winner names — split joined "A & B" strings
                      var winnerNames = [];
                      if (m.winnerTeam && m.winnerTeam.length > 0) {
                        m.winnerTeam.forEach(function(p) {
                          var n = typeof p === 'string' ? p : (p && (p.isBrand ? (p.brand_id || p.name) : (p.name || '')));
                          if (n) winnerNames.push(n);
                        });
                      } else if (m.winner) {
                        m.winner.split(' & ').forEach(function(n) { if (n.trim()) winnerNames.push(n.trim()); });
                      }
                      // Title info
                      var titleEntry = m.titleKey ? TITLES_DATABASE.find(function(t) { return t.id === m.titleKey; }) : null;
                      var titleDisplayName = m.titleName || (titleEntry && titleEntry.name) || null;
                      var titleShort = titleDisplayName
                        ? titleDisplayName.replace(/ Championship$/i,'').replace(/^(WWE|NXT) /i,'')
                        : (m.titleKey || null);
                      var titleRender = m.titleRender || (m.titleKey ? process.env.PUBLIC_URL + '/titles/' + m.titleKey + '-render.png' : null);
                      // Slot labels for PLE interbrand
                      var slotLabel = m.slotType === 'opener' ? { text: 'OPENER', color: '#2ecc71' }
                        : m.slotType === 'main_event' ? { text: 'MAIN EVENT', color: bc } : null;
                      var rowBg = m.isInterBrand ? bc + '08' : (i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent');
                      return (
                        <div key={i} style={{ padding: '9px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: rowBg }}>
                          {/* Meta badges */}
                          {(slotLabel || m.isInterBrand || m.matchType || m.titleKey) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '5px', flexWrap: 'wrap' }}>
                              {slotLabel && (
                                <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.37rem', color: slotLabel.color, letterSpacing: '1.5px', border: '1px solid ' + slotLabel.color + '55', borderRadius: '3px', padding: '1px 5px' }}>{slotLabel.text}</span>
                              )}
                              {m.isInterBrand && (
                                <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.37rem', color: 'rgba(255,255,255,0.3)', letterSpacing: '1px' }}>INTER-BRAND</span>
                              )}
                              {m.matchType && (
                                <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.37rem', color: '#e67e22', letterSpacing: '1px', border: '1px solid rgba(230,126,34,0.3)', borderRadius: '3px', padding: '1px 5px', background: 'rgba(230,126,34,0.08)' }}>
                                  {m.matchType.replace(/-/g,' ').toUpperCase()}
                                </span>
                              )}
                            </div>
                          )}
                          {/* Participants + rating */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ flex: 1 }}>
                              {teams.map(function(team, ti) {
                                var teamWon = winnerNames.length > 0 && team.some(function(n) {
                                  return winnerNames.some(function(wn) { return wn.toUpperCase() === (n||'').toUpperCase(); });
                                });
                                return (
                                  <span key={ti}>
                                    {ti > 0 && <span style={{ fontFamily: "'Anton',sans-serif", fontSize: '0.4rem', color: '#e74c3c', margin: '0 6px' }}>VS</span>}
                                    {team.map(function(name, ni) {
                                      return (
                                        <span key={ni}>
                                          {ni > 0 && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)', margin: '0 3px' }}>&amp;</span>}
                                          <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.52rem', color: teamWon ? '#2ecc71' : 'rgba(255,255,255,0.6)' }}>
                                            {(name||'?').toUpperCase()}
                                          </span>
                                        </span>
                                      );
                                    })}
                                  </span>
                                );
                              })}
                              {m.noContest && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: '#e74c3c', marginLeft: '8px' }}>NO CONTEST</span>}
                              {/* Title badge */}
                              {m.titleKey && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px', padding: '1px 6px 1px 3px', background: m.titleChange ? 'rgba(212,175,55,0.12)' : 'rgba(212,175,55,0.05)', border: '1px solid ' + (m.titleChange ? 'rgba(212,175,55,0.5)' : 'rgba(212,175,55,0.2)'), borderRadius: '4px', verticalAlign: 'middle' }}>
                                  {titleRender && <img loading="lazy" src={titleRender} alt="" style={{ height: '18px', objectFit: 'contain', opacity: m.titleChange ? 1 : 0.5 }} onError={function(e){e.target.style.display='none';}} />}
                                  <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.36rem', color: m.titleChange ? '#d4af37' : 'rgba(212,175,55,0.55)', letterSpacing: '1px' }}>
                                    {m.titleChange ? 'NEW CHAMP' : (titleShort ? titleShort.toUpperCase() : 'TITLE')}
                                  </span>
                                </span>
                              )}
                            </div>
                            {m.rating && <StarDisplay value={m.rating} color={m.isInterBrand ? (m.slotType === 'main_event' ? bc : '#2ecc71') : bc} />}
                          </div>
                          {/* Notes */}
                          {m.notes && (
                            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.35)', fontStyle: 'italic', marginTop: '4px' }}>"{m.notes}"</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Promos */}
                {cardFilter !== 'matches' && selectedWeek.promos && selectedWeek.promos.length > 0 && (
                  <div style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px', background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)', fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', letterSpacing: '2px', color: '#9b59b6' }}>PROMOS</div>
                    {selectedWeek.promos.map(function(pr, i) { return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}>
                        <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#9b59b6', background: 'rgba(155,89,182,0.12)', border: '1px solid rgba(155,89,182,0.3)', borderRadius: '3px', padding: '1px 6px', flexShrink: 0 }}>{pr.promoAction ? pr.promoAction.replace(/-/g, ' ').toUpperCase() : 'PROMO'}</div>
                        <div style={{ flex: 1, fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: 'rgba(255,255,255,0.7)' }}>
                          {(pr.callers || []).map(function(c) { return typeof c === 'string' ? c : (c.name || getWrestlerName(c, 'display') || '?'); }).join(' & ')}
                          {pr.targets && pr.targets.length > 0 && <span style={{ color: '#e74c3c' }}> → {pr.targets.map(function(t) { return typeof t === 'string' ? t : (t.name || getWrestlerName(t, 'display') || '?'); }).join(' & ')}</span>}
                        </div>
                      </div>
                    ); })}
                  </div>
                )}

                {/* Shake-Up recap */}
                {selectedWeek.shakeUp && selectedWeek.shakeUp.length > 0 && (
                  <div style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px', background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)', fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', letterSpacing: '2px', color: '#d4af37' }}>⇄ SHAKE-UP</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                      {selectedWeek.shakeUp.map(function(r, i) {
                        var hasAny = (r.leftPicks && r.leftPicks.length > 0) || (r.rightPicks && r.rightPicks.length > 0);
                        if (!hasAny) return null;
                        return (
                          <div key={i} style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: 'rgba(255,255,255,0.35)', letterSpacing: '1px' }}>
                              {r.isMyRound ? 'MY BRAND SENDS' : ('ROUND ' + (i + 1) + ' — ' + (r.leftBrand || '?') + ' ⇄ ' + (r.rightBrand || '?'))}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                              {(r.leftPicks || []).map(function(w) {
                                return (
                                  <span key={w.wrestler_id} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#e74c3c', background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '3px', padding: '1px 6px' }}>
                                    → {getWrestlerName(w, 'display')}
                                  </span>
                                );
                              })}
                              {(r.rightPicks || []).map(function(w) {
                                return (
                                  <span key={w.wrestler_id} style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.44rem', color: '#2ecc71', background: 'rgba(46,204,113,0.1)', border: '1px solid rgba(46,204,113,0.3)', borderRadius: '3px', padding: '1px 6px' }}>
                                    ← {getWrestlerName(w, 'display')}
                                  </span>
                                );
                              })}
                              {(r.cashLeft > 0 || r.cashRight > 0) && (
                                <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.4rem', color: '#d4af37', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '3px', padding: '1px 6px' }}>
                                  {r.cashLeft > 0 ? '-$' + r.cashLeft.toLocaleString('en-US') : ''}{r.cashRight > 0 ? '+$' + r.cashRight.toLocaleString('en-US') : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Big moments */}
                {((selectedWeek.titleChanges && selectedWeek.titleChanges.length > 0) || (selectedWeek.rivalryEvents && selectedWeek.rivalryEvents.length > 0)) && (
                  <div style={{ background: 'rgba(8,8,8,0.88)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px', background: 'rgba(0,0,0,0.4)', borderBottom: '1px solid rgba(255,255,255,0.06)', fontFamily: "'Anton',sans-serif", fontSize: '0.65rem', letterSpacing: '2px', color: '#fff' }}>🎬 BIG MOMENTS</div>
                    <div style={{ padding: '10px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {(selectedWeek.titleChanges || []).map((tc, i) => {
                        var tcEntry = tc.titleKey ? TITLES_DATABASE.find(function(t) { return t.id === tc.titleKey; }) : null;
                        var tcName = tc.titleName || (tcEntry && tcEntry.name) || tc.titleKey || 'Championship';
                        var tcRender = tc.titleRender || (tc.titleKey ? process.env.PUBLIC_URL + '/titles/' + tc.titleKey + '-render.png' : null);
                        var champList = (tc.newChampions && tc.newChampions.length > 0) ? tc.newChampions : (tc.newChampion ? [tc.newChampion] : []);
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '6px' }}>
                            {tcRender && <img loading="lazy" src={tcRender} alt={tcName} style={{ height: '44px', objectFit: 'contain', filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.5))', flexShrink: 0 }} onError={function(e){e.target.style.display='none';}} />}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.38rem', color: 'rgba(212,175,55,0.6)', letterSpacing: '1.5px' }}>{tcName.toUpperCase()}</span>
                              <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.5rem', color: '#d4af37' }}>
                                NEW CHAMPION{champList.length > 1 ? 'S' : ''}: {champList.map(function(c){return (c||'').toUpperCase();}).join(' & ')}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {(selectedWeek.rivalryEvents || []).map((re, i) => {
                        const colors = { create: '#e74c3c', heatup: '#e67e22', cooldown: '#3498db', end: '#888' };
                        const labels = { create: '⚡ RIVALRY CREATED', heatup: '🔥 HEATED UP', cooldown: '❄️ COOLED DOWN', end: '✓ ENDED' };
                        return (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '5px' }}>
                            <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.46rem', color: colors[re.impact] || '#fff' }}>{labels[re.impact] || re.impact}</span>
                            {(re.participants || []).length > 0 && <span style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.42rem', color: 'rgba(255,255,255,0.3)' }}>— {re.participants.join(' & ')}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ fontFamily: "'Monda',sans-serif", fontSize: '0.55rem', color: 'rgba(255,255,255,0.2)', letterSpacing: '2px', textAlign: 'center' }}>
                  ← SELECT A WEEK<br/>TO SEE DETAILS
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
      )}
    </div>
  );
}
